import { AnimatedSprite } from '@pixi/react';
import { getZIndex } from '../../utils/zindex.utils';
import { useSpritesStore } from '../../store/spritesSlice';
import { useGameStore } from '../../store/gameSlice';
import { dungeonService } from '../../services/DungeonService';
import { tileWidth, tileHeight } from '../../data/tileset.config';
import { Spritesheet, Texture } from 'pixi.js';
import { PlacedLocation } from '../../types/map.types';
import { LOCATION_REGISTRY } from '../../data/locations.config';
import Placeholder from './Placeholder';
import { missingAssetService } from '../../services/MissingAssetService';

export const LocationRenderer = () => {
  const textures = useSpritesStore((s) => s.textures);
  const zoomFactor = useGameStore((s) => s.zoomFactor);

  const raw = dungeonService.locationService.getPlacedLocations();
  const locations = raw.filter((l) => !!l.sprite);

  // Flat output: each sprite is a direct child of the Y-sortable Container so
  // it depth-sorts at the same level as tiles, items and entities.
  return (
    <>
      {locations.map((loc) => (
        <LocationSprite
          key={`${loc.id}:${loc.x}:${loc.y}`}
          location={loc}
          textures={textures}
          zoomFactor={zoomFactor}
        />
      ))}
    </>
  );
};

interface LocationSpriteProps {
  location: PlacedLocation;
  textures: Record<string, Spritesheet>;
  zoomFactor: number;
}

const LocationSprite = ({
  location,
  textures,
  zoomFactor,
}: LocationSpriteProps) => {
  if (!location.sprite) {
    return null;
  }
  const spriteKey = location.direction
    ? `${location.sprite}-${location.direction}`
    : location.sprite;
  const spritesheet = textures[spriteKey];
  const frames: Texture[] | undefined = spritesheet?.animations?.[spriteKey];

  const isDestroyed =
    dungeonService.locationService.isLocationDestroyed(location);

  const isBreaking =
    dungeonService.locationService.isLocationBreaking(location);

  // Anchored at the footprint's bottom-center to allow vertical overflow
  // (artHeight > height) without shifting the feet.
  const def = LOCATION_REGISTRY[location.id];
  const artWidth = def?.artWidth ?? location.width;
  const artHeight = def?.artHeight ?? location.height;
  const artAnchor = def?.artAnchor ?? [0.5, 1];

  const footprintCenterX = location.x + (location.width - 1) / 2;
  const footprintBottomY = location.y - (location.height - 1);

  const x = footprintCenterX * tileWidth * zoomFactor;
  const y = (-footprintBottomY * tileHeight + tileHeight / 2) * zoomFactor;
  const w = artWidth * tileWidth * zoomFactor;
  const h = artHeight * tileHeight * zoomFactor;
  const zIndex = getZIndex({
    type: 'location',
    posY: location.y,
    height: location.height,
  });

  if (!frames || frames.length === 0) {
    missingAssetService.report(spriteKey, 'location');
    return (
      <Placeholder
        x={x}
        y={y}
        width={w}
        height={h}
        anchor={artAnchor as [number, number]}
        zIndex={zIndex}
      />
    );
  }

  const handleComplete = () => {
    dungeonService.locationService.clearLocationBreaking(location);
  };

  return (
    <AnimatedSprite
      textures={frames}
      x={x}
      y={y}
      width={w}
      height={h}
      anchor={artAnchor}
      zIndex={zIndex}
      loop={false}
      isPlaying={isBreaking}
      initialFrame={isDestroyed ? frames.length - 1 : 0}
      animationSpeed={location.spriteAnimationSpeed ?? 0.15}
      onComplete={handleComplete}
    />
  );
};
